import React, { useState, useMemo, useEffect } from 'react';
import {
  ChevronRightIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  ClipboardDocumentIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import Pagination from './Pagination';
import { scrubSensitiveDeep } from '../utils/eventUtils';

/**
 * RawLogViewer - List of events with expandable/collapsible JSON detail.
 *
 * Props:
 * @param {Array<object>} events - Array of sign-in event objects
 *
 * Expected event shape:
 * {
 *   Timestamp: "2024-02-10T08:15:00Z",
 *   User: "user@contoso.com",
 *   Application: "Microsoft Teams",
 *   IPAddress: "203.0.113.50",
 *   Status: "Success",
 *   RawLog: { ... full raw event object ... }
 * }
 */

function formatTimestamp(ts) {
  if (!ts) return '\u2014';
  try {
    return format(parseISO(ts), 'MMM d, yyyy HH:mm:ss');
  } catch {
    return ts;
  }
}

export default function RawLogViewer({ events = [], revealSubjects = true }) {
  const [expandedItems, setExpandedItems] = useState(new Set());

  // Sensitive detail (subjects, file and attachment names — potential PHI/PII) is
  // scrubbed from the raw JSON unless the case has been revealed (a logged action).
  // Display/copy only — the source object is untouched.
  //
  // This covers BOTH the rendered <pre> and the Copy JSON button, because both read
  // jsonForDisplay. It has to: Exchange events carry NO RawLog, so `ev.RawLog || ev`
  // falls through to the event itself and any unscrubbed field on it is rendered
  // on screen AND placed on the clipboard while the case is masked.
  const jsonForDisplay = (ev) => {
    const obj = ev.RawLog || ev;
    if (revealSubjects) return obj;
    try { return scrubSensitiveDeep(JSON.parse(JSON.stringify(obj))); } catch { return obj; }
  };
  const [filterQuery, setFilterQuery] = useState('');
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const toggleExpand = (index) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const filteredEvents = useMemo(() => {
    if (!filterQuery.trim()) return events;
    const q = filterQuery.toLowerCase();
    return events.filter((event) => {
      return JSON.stringify(event).toLowerCase().includes(q);
    });
  }, [events, filterQuery]);

  useEffect(() => {
    setCurrentPage(1);
    setExpandedItems(new Set());
  }, [filteredEvents]);

  const totalPages = Math.max(1, Math.ceil(filteredEvents.length / pageSize));
  const pagedEvents = filteredEvents.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const expandAll = () => {
    setExpandedItems(new Set(pagedEvents.map((_, i) => i)));
  };

  const collapseAll = () => {
    setExpandedItems(new Set());
  };

  const handleCopyJson = async (index, event) => {
    const jsonStr = JSON.stringify(jsonForDisplay(event), null, 2);
    try {
      await navigator.clipboard.writeText(jsonStr);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch {
      // Fallback: no-op in non-secure context
    }
  };

  if (events.length === 0) {
    return (
      <div className="card text-center py-12">
        <DocumentTextIcon className="h-10 w-10 text-gray-700 mx-auto mb-3" />
        <p className="text-gray-500">No raw log events found.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <input
            type="text"
            placeholder="Search logs..."
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
            className="input-field pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">
            {filteredEvents.length} of {events.length} event
            {events.length !== 1 ? 's' : ''}
          </span>
          <button
            onClick={expandAll}
            className="text-xs text-gray-500 hover:text-gray-300 px-2 py-1 rounded hover:bg-gray-800 transition-colors"
          >
            Expand All
          </button>
          <button
            onClick={collapseAll}
            className="text-xs text-gray-500 hover:text-gray-300 px-2 py-1 rounded hover:bg-gray-800 transition-colors"
          >
            Collapse All
          </button>
        </div>
      </div>

      {/* Event List */}
      <div className="space-y-1">
        {pagedEvents.map((event, index) => {
          const isExpanded = expandedItems.has(index);
          return (
            <div
              key={index}
              className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden"
            >
              {/* Header Row */}
              <button
                onClick={() => toggleExpand(index)}
                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-800/50 transition-colors text-left"
              >
                <ChevronRightIcon
                  className={`h-4 w-4 text-gray-600 flex-shrink-0 transition-transform ${
                    isExpanded ? 'rotate-90' : ''
                  }`}
                />
                {event._type && (
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded flex-shrink-0 ${
                    event._type === 'Exchange'
                      ? 'bg-purple-500/20 text-purple-400'
                      : 'bg-blue-500/20 text-blue-400'
                  }`}>
                    {event._type}
                  </span>
                )}
                <span className="text-xs font-mono text-gray-500 flex-shrink-0 w-40">
                  {formatTimestamp(event.EventTimeUTC)}
                </span>
                <span className="text-xs text-gray-400 flex-shrink-0 w-32 truncate">
                  {event.ApplicationName || event.Operation || '\u2014'}
                </span>
                <span className="text-xs font-mono text-gray-500 flex-shrink-0">
                  {event.IPAddress || '\u2014'}
                </span>
                <span
                  className={`text-xs ml-auto flex-shrink-0 ${
                    event.ResultStatus?.toLowerCase() === 'success'
                      ? 'text-green-500'
                      : event.ResultStatus?.toLowerCase() === 'failure' ||
                        event.ResultStatus?.toLowerCase() === 'failed'
                      ? 'text-red-500'
                      : 'text-gray-500'
                  }`}
                >
                  {event.ResultStatus || ''}
                </span>
              </button>

              {/* Expanded JSON Detail */}
              {isExpanded && (
                <div className="border-t border-gray-800 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-500">
                      Raw Event Data
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopyJson(index, event);
                      }}
                      className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300 px-2 py-1 rounded hover:bg-gray-800 transition-colors"
                    >
                      {copiedIndex === index ? (
                        <>
                          <CheckIcon className="h-3.5 w-3.5 text-green-400" />
                          <span className="text-green-400">Copied</span>
                        </>
                      ) : (
                        <>
                          <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                          Copy JSON
                        </>
                      )}
                    </button>
                  </div>
                  <pre className="bg-gray-950 border border-gray-700 rounded-lg p-3 text-xs text-gray-300 overflow-x-auto max-h-96 overflow-y-auto font-mono leading-relaxed">
                    {JSON.stringify(jsonForDisplay(event), null, 2)}
                  </pre>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-2 card p-0 overflow-hidden">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={filteredEvents.length}
          onPageChange={(p) => { setCurrentPage(p); setExpandedItems(new Set()); }}
          onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); setExpandedItems(new Set()); }}
        />
      </div>
    </div>
  );
}
